import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Modal, Button } from 'antd';
import dayjs from '../../../../utils/dayjs';
import Card from '../../../../components/Card/Card';
import Table from '../../../../components/Table/Table';
import { fetchTransactions } from './TransactionActions';
import { transactionsSelector, loadingTransactionsSelector } from './TransactionSelectors';
import RenewTransactionForm from './components/RenewTransactionForm';

const SHOW_TRANSACTION_RENEW_COLUMN = false;

function Transaction({ transactions, loading, fetchTransactions }) {
  const [renewingTransaction, setRenewingTransaction] = useState(null);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  const typeFilters = useMemo(() => {
    const unique = Array.from(
      new Set((transactions || []).map((row) => row.type).filter(Boolean)),
    ).sort((a, b) => a.localeCompare(b));
    return unique.map((value) => ({ text: value, value }));
  }, [transactions]);

  const columns = [
    {
      key: 'type',
      title: 'Type',
      dataIndex: 'type',
      filters: typeFilters.length ? typeFilters : undefined,
      onFilter: typeFilters.length ? (value, record) => record.type === value : undefined,
      // Show BE type as-is (e.g. "Purchase", "Transfer", "Purchase (Admin Assigned)").
      render: (type) => <span>{type || '-'}</span>,
    },
    {
      key: 'description',
      title: 'Description',
      dataIndex: 'description',
      render: (text) => <span>{text || '-'}</span>,
    },
    {
      key: 'date',
      title: 'Date',
      render: (text, record) => (
        <span>{record.date ? dayjs(record.date).format('DD/MM/YYYY') : '-'}</span>
      ),
      sorter: (a, b) => new Date(a.date || 0).getTime() - new Date(b.date || 0).getTime(),
    },
    {
      key: 'amount',
      title: 'Amount',
      render: (text, record) => {
        if (record.amount == null || Number.isNaN(Number(record.amount))) return <span>-</span>;
        return <span>{record.amount}</span>;
      },
    },
    {
      key: 'initiator',
      title: 'Initiator',
      dataIndex: 'initiator',
      render: (text) => <span>{text || '-'}</span>,
    },
    {
      key: 'expiryDate',
      title: 'Expiry Date',
      render: (text, record) => (
        <span>{record.expiryDate ? dayjs(record.expiryDate).format('DD/MM/YYYY') : '-'}</span>
      ),
      align: 'center',
    },
    {
      key: 'receipt',
      title: 'Receipt',
      render: (text, record) => <span>{record.receiptNumber || '-'}</span>,
      align: 'center',
    },
    {
      key: 'renew',
      title: 'Renew',
      width: 100,
      align: 'center',
      render: (text, record) =>
        record.isRenewable ? (
          <Button size="small" onClick={() => setRenewingTransaction(record)}>
            Renew
          </Button>
        ) : (
          <span>-</span>
        ),
    },
  ].filter((column) => SHOW_TRANSACTION_RENEW_COLUMN || column.key !== 'renew');

  return (
    <div className="component-wrapper">
      <Card title="Transaction History">
        <Table
          title="Transactions"
          loading={loading}
          columns={columns}
          dataSource={transactions}
          rowKey="transactionId"
          searchField={['type', 'description', 'initiator', 'receiptNumber', 'date']}
          style={{ paddingBottom: '60px' }}
        />
      </Card>

      {SHOW_TRANSACTION_RENEW_COLUMN && (
        <Modal
          title="Renew transaction"
          visible={Boolean(renewingTransaction)}
          footer={null}
          maskClosable={false}
          destroyOnClose
          onCancel={() => setRenewingTransaction(null)}
        >
          {renewingTransaction && (
            <RenewTransactionForm
              transaction={renewingTransaction}
              close={() => setRenewingTransaction(null)}
            />
          )}
        </Modal>
      )}
    </div>
  );
}

const mapStateToProps = (state) => ({
  transactions: transactionsSelector(state),
  loading: loadingTransactionsSelector(state),
});

const mapDispatchToProps = { fetchTransactions };

export default connect(mapStateToProps, mapDispatchToProps)(Transaction);
